// src/components/captures/CaptureInputSection.tsx
import React, { useState, useEffect, useRef } from 'react';
import { PenLine, Maximize2 } from '../common/icons';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { eventBus, EVENTS } from '../../utils/eventBus';
import { logError } from '../../utils/logger';

interface CaptureInputSectionProps {
  onSubmit?: (content: string) => Promise<void>;
  isSubmitting?: boolean;
  placeholder?: string;
  onExpand?: (content: string) => void; // 新增：點擊展開圖標時的回調，傳入當前內容
}

const CaptureInputSection: React.FC<CaptureInputSectionProps> = ({
  onSubmit,
  isSubmitting = false,
  placeholder = "What's on your mind? Capture it here...",
  onExpand,
}) => {
  const [content, setContent] = useState('');
  const hasAutoExpanded = useRef(false); // 追蹤是否已自動展開

  // 檢測是否為行動版
  const isMobile = useMediaQuery('(max-width: 640px)');

  /**
   * 字數檢測 - 超過閾值自動展開 Modal
   */
  useEffect(() => {
    if (!content || !onExpand || hasAutoExpanded.current) return;

    // Desktop: 80 字, Mobile: 20 字
    const threshold = isMobile ? 20 : 80;

    if (content.length >= threshold) {
      hasAutoExpanded.current = true; // 標記已自動展開
      onExpand(content);
    }
  }, [content, isMobile, onExpand]);

  /**
   * 重置自動展開標記（當內容被清空時）
   */
  useEffect(() => {
    if (content === '') {
      hasAutoExpanded.current = false;
    }
  }, [content]);

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    try {
      if (onSubmit) {
        await onSubmit(content.trim());
      }
      // 送出成功後通知 Lesson1Guide 隱藏輸入框 Tooltip
      eventBus.publish(EVENTS.LESSON.CAPTURE_INPUT_INTERACTED);
      setContent('');
    } catch (error) {
      logError('Error submitting capture:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const canSubmit = content.trim() && !isSubmitting;

  return (
    <div
      className={isMobile
        ? "mb-4 pb-3 border-b border-gray-200"
        : "bg-white border border-gray-200 rounded-xl p-5 mb-6"
      }
    >
      <div className="flex gap-2">
        {/* 輸入框 */}
        <div className="flex-1 relative">
          <input
            data-lesson-anchor="capture-input"
            type="text"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (e.target.value.length === 1) {
                // 第一個字元時發出事件，隱藏 Lesson Tooltip
                eventBus.publish(EVENTS.LESSON.CAPTURE_INPUT_INTERACTED);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isSubmitting}
            className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-lg text-base bg-gray-50 transition-all focus:outline-none focus:border-sky-600 focus:bg-white focus:ring-3 focus:ring-sky-100"
          />
          {/* 展開圖標 */}
          {onExpand && (
            <button
              type="button"
              onClick={() => {
                eventBus.publish(EVENTS.LESSON.CAPTURE_INPUT_INTERACTED);
                onExpand(content);
              }}
              className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-all duration-200"
              aria-label="Expand editor"
              disabled={isSubmitting}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Capture 按鈕 */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="btn-accent btn-sm"
          title="Capture"
        >
          <PenLine className={`w-4 h-4 ${isSubmitting ? 'animate-pulse' : ''}`} />
          <span className="hidden sm:inline">
            {isSubmitting ? 'Capturing...' : 'Capture'}
          </span>
        </button>
      </div>
    </div>
  );
};

export default CaptureInputSection;